import { supabase } from './db';
import { clearCache } from './local/cache';
import { identificadorParaEmail } from './usuario';

/**
 * Sessão e identidade do usuário.
 *
 * Isolado porque é o ponto que mais muda quando o app passa a funcionar
 * offline: hoje `currentUser()` vai à rede, e mais adiante vai precisar
 * responder a partir da sessão guardada no dispositivo.
 */

export const getSession = async () => (await supabase.auth.getSession()).data.session;

export const getAccessToken = async () => (await getSession())?.access_token || null;

/**
 * Usuário da sessão guardada, SEM ir à rede.
 *
 * `supabase.auth.getUser()` faz uma chamada HTTP para validar o token no
 * servidor. Isso quebrava todo cadastro offline: a identidade era buscada
 * antes da escrita, a chamada falhava sem internet, e o lançamento morria ali
 * — nem chegava na fila.
 *
 * `getSession()` lê do armazenamento local e responde sem rede. Para carimbar
 * `created_by` é o suficiente: quem valida de verdade quem é o usuário é o RLS
 * do banco, no momento em que a escrita chega.
 */
export const currentUser = async () => (await getSession())?.user || null;

export const currentUserId = async () => (await currentUser())?.id || null;

/** Valida o token no servidor. Só para quando isso for mesmo necessário. */
export const currentUserOnline = async () => (await supabase.auth.getUser()).data.user;

/**
 * Entrada por nome de usuário.
 *
 * O que chega aqui é o que a pessoa digitou — `maria` ou, nas contas antigas,
 * o e-mail inteiro. `identificadorParaEmail` resolve os dois casos; do
 * Supabase para baixo continua sendo login por e-mail, como sempre foi.
 */
export const signIn = (identificador, password) =>
  supabase.auth.signInWithPassword({
    email: identificadorParaEmail(identificador),
    password,
  });

/**
 * Sai e apaga a cópia local.
 *
 * O custo é perder o acesso offline até o próximo login com sinal. É o certo:
 * a cópia carrega dados do laboratório, e quem saiu não deve continuar
 * enxergando — ainda mais num aparelho compartilhado entre técnicos.
 */
export const signOut = async () => {
  await clearCache();
  return supabase.auth.signOut();
};

export const onAuthChange = (callback) => supabase.auth.onAuthStateChange(callback);

/**
 * Mantém o MESMO objeto de usuário enquanto for a mesma pessoa.
 *
 * O Supabase emite um `user` novo a cada renovação de token e a cada vez que a
 * aba volta ao foco. São objetos diferentes com conteúdo igual — e para o
 * React, identidade diferente é mudança: todo `useEffect([user])` da árvore
 * dispara de novo.
 *
 * No app isso tinha consequência visível. O efeito que descobre se quem entrou
 * é cliente levanta `verificandoCliente`, e enquanto ele está levantado o App
 * devolve a tela de carregamento — que DESMONTA o FIVSystem inteiro. O
 * trabalho em tela sumia e voltava em "Verificando acesso do usuário", sozinho,
 * no meio do uso.
 *
 * Comparar por id corta na raiz, em vez de caçar cada consumidor.
 */
export const mesmaPessoa = (anterior, novo) =>
  (anterior?.id && anterior.id === novo?.id ? anterior : (novo ?? null));
